import React, { useEffect, useState } from 'react';
import { useUserData } from '../contexts/UserDataContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

const grammarTopics = [
    { key: 'noun', label: 'Noun' },
    { key: 'verb', label: 'Verb' },
    { key: 'adjective', label: 'Adjective' },
    { key: 'adverb', label: 'Adverb' },
    { key: 'pronoun', label: 'Pronoun' },
    { key: 'preposition', label: 'Preposition' },
    { key: 'conjunction', label: 'Conjunction' },
    { key: 'interjection', label: 'Interjection' },
    { key: 'presentSimple', label: 'Present Simple' },
    { key: 'presentContinuous', label: 'Present Continuous' },
    { key: 'presentPerfect', label: 'Present Perfect' },
    { key: 'pastSimple', label: 'Past Simple' },
    { key: 'pastContinuous', label: 'Past Continuous' },
    { key: 'pastPerfect', label: 'Past Perfect' },
    { key: 'futureSimple', label: 'Future Simple' },
    { key: 'futureContinuous', label: 'Future Continuous' },
    { key: 'futurePerfect', label: 'Future Perfect' },
    { key: 'voiceNarration', label: 'Voice & Narration' },
    { key: 'modals', label: 'Modals' },
    { key: 'auxiliaries', label: 'Auxiliaries' },
];

export default function CompletedGrammarPage() {
    const { userData } = useUserData();
    const { currentUser } = useAuth();
    const selectedLanguage = userData?.selectedLanguage || 'en';
    console.log('CompletedGrammarPage - userData:', userData);
    console.log('CompletedGrammarPage - currentUser:', currentUser);
    console.log('CompletedGrammarPage - selectedLanguage:', selectedLanguage);
    const [selectedTopic, setSelectedTopic] = useState(grammarTopics[0].key);
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [userCount, setUserCount] = useState<number>(0);
    const PAGE_SIZE = 5;
    const [page, setPage] = useState(0);

    useEffect(() => {
        if (!currentUser?.uid) return;
        setItems([]); // Clear old data immediately
        setLoading(true);
        api.getCompletedGrammar(currentUser.uid, selectedTopic)
            .then(res => {
                setItems(res || []);
                setPage(0); // Reset to first page when topic changes
            })
            .catch(error => {
                console.error('Error fetching completed grammar:', error);
            })
            .finally(() => setLoading(false));

        // Remove the unnecessary count API call
        // api.getCompletedGrammarCount(currentUser.uid, selectedTopic)
        //     .then(count => {
        //         setUserCount(count);
        //     })
        //     .catch(err => {
        //         console.error('Error fetching completed grammar count:', err);
        //         setUserCount(0);
        //     });
    }, [selectedTopic, currentUser?.uid]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-100 p-6">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-4xl font-extrabold tracking-tight drop-shadow-lg mb-6 text-center">
                    <span className="bg-gradient-to-r from-blue-500 via-green-500 to-pink-400 bg-clip-text text-transparent">Completed Grammar</span>
                </h1>
            </div>
            <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl p-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
                    {/* Removed Selected Language text */}
                    <div className="text-lg font-bold text-pink-700">Total: {items.length}</div>
                </div>
                <div className="flex flex-wrap gap-2 justify-center mb-6">
                    {grammarTopics.map(topic => (
                        <button
                            key={topic.key}
                            className={`px-6 py-2 rounded-full font-semibold text-base transition-all duration-200 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-300 ${selectedTopic === topic.key ? 'bg-gradient-to-r from-blue-500 via-green-500 to-pink-500 text-white scale-105' : 'bg-blue-100 text-blue-800 hover:bg-gradient-to-r hover:from-blue-200 hover:via-green-100 hover:to-pink-100 hover:text-blue-900'}`}
                            onClick={() => setSelectedTopic(topic.key)}
                        >
                            {topic.label}
                        </button>
                    ))}
                </div>
                {loading ? (
                    <div className="text-center text-lg text-blue-500 font-semibold py-12">Loading...</div>
                ) : items.length === 0 ? (
                    <div className="text-center text-gray-500 font-semibold py-12">No completed grammar items in this topic! (Total: {userCount})</div>
                ) : (
                    <>
                        <div className="flex flex-wrap gap-6 justify-center items-stretch">
                            {items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((item, idx) => (
                                <div key={item.itemId || idx} className="flex-1 max-w-xs min-w-[250px] bg-white/80 backdrop-blur-2xl border border-blue-200 rounded-3xl p-6 shadow-xl flex flex-col items-center gap-4 relative">
                                    <div className="w-full text-center mb-2">
                                        {/* Show selected language on top, English below if not English */}
                                        <div className="text-2xl font-bold mb-2 text-gray-900">
                                            {item[selectedLanguage] || <span className='italic text-red-500'>Grammar example not found (check itemId format in completed collection)</span>}
                                        </div>
                                        {selectedLanguage !== 'en' && item.en && (
                                            <div className="text-base text-gray-500">{item.en}</div>
                                        )}
                                    </div>
                                    {item.translation && (
                                        <div className="text-lg text-blue-700 font-semibold mb-2">{item.translation}</div>
                                    )}
                                    {item.subtopic && (
                                        <div className="text-base text-gray-500 italic mb-2">{item.subtopic}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                        {/* Pagination controls */}
                        {items.length > PAGE_SIZE && (
                            <div className="flex justify-center gap-4 mt-6">
                                <button
                                    className="px-6 py-2 rounded-full bg-gradient-to-r from-gray-200 to-gray-400 text-gray-800 font-semibold shadow-lg hover:from-gray-300 hover:to-gray-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
                                    onClick={() => setPage(p => Math.max(0, p - 1))}
                                    disabled={page === 0}
                                >
                                    Previous
                                </button>
                                <span className="px-4 py-2 text-gray-700 font-semibold">Page {page + 1} of {Math.ceil(items.length / PAGE_SIZE)}</span>
                                <button
                                    className="px-6 py-2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 text-white font-semibold shadow-lg hover:from-blue-500 hover:to-blue-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50"
                                    onClick={() => setPage(p => (p + 1 < Math.ceil(items.length / PAGE_SIZE) ? p + 1 : p))}
                                    disabled={page + 1 >= Math.ceil(items.length / PAGE_SIZE)}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
} 